/**
 * Include your custom JavaScript here.
 *
 * We make some recommendations:
 *
 * You should use the Theme Events system to hook into various parts of the theme: https://support.maestrooo.com/article/420-theme-events-system
 * ES6 is supported in the theme so your code can be written using the new ES6 syntax.
 * We're using Web Components heavily in our theme. We recommend familiarizing yourself with this concept and browser API.
 * Please use this file to add your custom JavaScript instead of modifying the theme files, as this file won't be overridden during theme updates.
 */

/**
 * DC.ONE stock-aware size filtering
 *
 * Shopify Search & Discovery must keep the Availability filter enabled so
 * Shopify recognises filter.v.availability. The customer-facing Availability
 * control is hidden, while an in-stock condition is automatically submitted
 * whenever one or more Size values are selected.
 */
(() => {
  const SIZE_FILTER_NAME = 'filter.v.option.size';
  const SIZE_FILTER_SELECTOR =
    `input[type="checkbox"][name="${SIZE_FILTER_NAME}" i]`;
  const AVAILABILITY_NAME = 'filter.v.availability';
  const AVAILABILITY_SELECTOR =
    `input[type="checkbox"][name="${AVAILABILITY_NAME}" i]`;
  const MARKER_ATTRIBUTE = 'data-dc-size-availability';

  function hideCustomerAvailabilityControl(form) {
    if (!form) return;

    form.querySelectorAll(AVAILABILITY_SELECTOR).forEach((input) => {
      input.disabled = true;

      const group = input.closest('.collection__filter-group');

      if (group) {
        group.hidden = true;
        group.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function syncForm(form) {
    if (!form) return;

    hideCustomerAvailabilityControl(form);

    const sizeInputs = Array.from(
      form.querySelectorAll(SIZE_FILTER_SELECTOR),
    );

    if (!sizeInputs.length) return;

    let availabilityInput = form.querySelector(
      `input[${MARKER_ATTRIBUTE}]`,
    );

    if (!availabilityInput) {
      availabilityInput = document.createElement('input');
      availabilityInput.type = 'hidden';
      availabilityInput.name = AVAILABILITY_NAME;
      availabilityInput.value = '1';
      availabilityInput.setAttribute(MARKER_ATTRIBUTE, '');
      form.appendChild(availabilityInput);
    }

    availabilityInput.disabled = !sizeInputs.some(
      (input) => input.checked,
    );
  }

  function syncAllForms() {
    document.querySelectorAll('form').forEach((form) => {
      hideCustomerAvailabilityControl(form);

      if (form.querySelector(SIZE_FILTER_SELECTOR)) {
        syncForm(form);
      }
    });
  }

  function normaliseSizeAvailabilityUrl() {
    const url = new URL(window.location.href);
    const hasSize = Array.from(url.searchParams.keys()).some(
      (key) => key.toLowerCase() === SIZE_FILTER_NAME,
    );
    const availabilityValues = url.searchParams.getAll(
      AVAILABILITY_NAME,
    );
    const hasCorrectAvailability =
      availabilityValues.length === 1 &&
      availabilityValues[0] === '1';

    if (hasSize && !hasCorrectAvailability) {
      url.searchParams.delete(AVAILABILITY_NAME);
      url.searchParams.set(AVAILABILITY_NAME, '1');
      window.location.replace(url.toString());
      return true;
    }

    if (!hasSize && availabilityValues.length) {
      url.searchParams.delete(AVAILABILITY_NAME);
      window.location.replace(url.toString());
      return true;
    }

    return false;
  }

  let syncScheduled = false;

  function scheduleSync() {
    if (syncScheduled) return;

    syncScheduled = true;
    window.requestAnimationFrame(() => {
      syncScheduled = false;
      syncAllForms();
    });
  }

  document.addEventListener(
    'change',
    (event) => {
      if (
        event.target instanceof HTMLInputElement &&
        event.target.matches(SIZE_FILTER_SELECTOR)
      ) {
        syncForm(event.target.form);
      }
    },
    true,
  );

  function initialise() {
    if (normaliseSizeAvailabilityUrl()) return;
    syncAllForms();

    new MutationObserver(scheduleSync).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, {
      once: true,
    });
  } else {
    initialise();
  }
})();

/**
 * DC.ONE WhatsApp product enquiry button styles
 */
(() => {
  const styleId = 'dc-whatsapp-enquiry-button-styles';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .dc-whatsapp-enquiry-button {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 9px;
      width: 100% !important;
      margin-top: 12px;
      min-height: 48px;
      padding: 12px 18px;
      background: #25d366 !important;
      border: 1px solid #20bd5a !important;
      color: #ffffff !important;
      font-weight: 700;
      text-align: center;
      text-decoration: none !important;
      line-height: 1.2;
      transition: background-color .2s ease, border-color .2s ease, transform .2s ease;
    }

    .dc-whatsapp-enquiry-button:hover,
    .dc-whatsapp-enquiry-button:focus {
      background: #1ebe5d !important;
      border-color: #1aa952 !important;
      color: #ffffff !important;
      transform: translateY(-1px);
    }

    .dc-whatsapp-enquiry-button__icon {
      display: block !important;
      flex: 0 0 20px !important;
      width: 20px !important;
      min-width: 20px !important;
      max-width: 20px !important;
      height: 20px !important;
      min-height: 20px !important;
      max-height: 20px !important;
      border: 0 !important;
    }

    .dc-whatsapp-enquiry-button span {
      display: inline-block;
    }
  `;

  document.head.appendChild(style);
})();

/**
 * DC.ONE WhatsApp product enquiry button
 */
(() => {
  let attempts = 0;
  const maxAttempts = 40;

  function addWhatsAppEnquiryButton() {
    if (!document.body?.classList.contains('template-product')) {
      return false;
    }

    const infoColumn = document.querySelector(
      '.product-block-list__item--info',
    );

    if (!infoColumn) return false;

    if (infoColumn.querySelector('.dc-whatsapp-enquiry-button')) {
      return true;
    }

    const paymentContainer = infoColumn.querySelector(
      '.product-form__payment-container',
    );
    const titleElement = infoColumn.querySelector('.product-meta__title');

    if (!paymentContainer || !titleElement) return false;

    const productName = titleElement.textContent.trim().replace(/\s+/g, ' ');
    const productUrl = window.location.origin + window.location.pathname;
    const message =
      `I would like to enquire about ${productName}\n${productUrl}`;

    const button = document.createElement('a');
    button.className =
      'dc-whatsapp-enquiry-button button button--full';
    button.href =
      `https://wa.me/27609751832?text=${encodeURIComponent(message)}`;
    button.target = '_blank';
    button.rel = 'noopener noreferrer';
    button.setAttribute(
      'aria-label',
      `Enquire about ${productName} on WhatsApp`,
    );
    button.innerHTML =
      '<svg class="dc-whatsapp-enquiry-button__icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20 11.5a8 8 0 0 1-8.4 8L7 21l1.5-4.1A8 8 0 1 1 20 11.5Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.7 9.1c.4 2.4 2 4 4.4 4.5l1.2-1.2 2 .9c.3.1.4.4.3.7-.3 1-1.2 1.6-2.2 1.5-4.2-.4-7.4-3.6-7.8-7.8-.1-1 .5-1.9 1.5-2.2.3-.1.6 0 .7.3l.9 2-1 1.3Z" fill="currentColor"/></svg><span>Enquire about this product</span>';

    paymentContainer.insertAdjacentElement('afterend', button);
    return true;
  }

  function tryAddButton() {
    if (addWhatsAppEnquiryButton()) return;

    attempts += 1;

    if (attempts < maxAttempts) {
      window.setTimeout(tryAddButton, 250);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryAddButton);
  } else {
    tryAddButton();
  }

  document.addEventListener('shopify:section:load', () => {
    attempts = 0;
    tryAddButton();
  });
})();

/**
 * Microsoft Clarity — existing DC.ONE project
 */
(() => {
  if (window.clarity) return;

  (function(c, l, a, r, i, t, y) {
    c[a] = c[a] || function() {
      (c[a].q = c[a].q || []).push(arguments);
    };
    t = l.createElement(r);
    t.async = true;
    t.src = `https://www.clarity.ms/tag/${i}`;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', 'fpy1qsktk2');
})();

/**
 * Subscribers web push — existing DC.ONE site
 */
(() => {
  const src = 'https://cdn.subscribers.com/assets/subscribers.js';

  if (document.querySelector(`script[src="${src}"]`)) return;

  window.subscribersSiteId = '69c3af2a-58f6-4f70-9d6c-ab886a852dd1';
  window.subscribersServiceWorkerPath = '/apps/firebase-messaging-sw';
  window.subscribersServiceWorkerScope = '/apps/';

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
})();

/**
 * DC.ONE premium product-card pricing
 *
 * Yellow is reserved for sale badges, ratings and small accents. Product
 * prices stay black for stronger readability and a more premium hierarchy.
 */
(() => {
  const styleId = 'dc-product-card-price-styles';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .product-item__price-list .price,
    .product-item__price-list .price--highlight {
      color: #0b0b0b !important;
    }

    .product-item__price-list .price--highlight {
      font-weight: 600;
    }

    .product-item__price-list .price--compare {
      color: #777777 !important;
      font-weight: 400;
    }
  `;

  document.head.appendChild(style);
})();

/**
 * DC.ONE featured-product pricing and footer contact visibility
 */
(() => {
  const styleId = 'dc-featured-price-footer-contact-styles';

  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .product-form__info-list .price,
    .product-form__info-list .price--highlight {
      color: #0b0b0b !important;
    }

    .product-form__info-list .price--highlight {
      font-weight: 600;
    }

    .product-form__info-list .price--compare {
      color: #777777 !important;
      font-weight: 400;
    }

    .footer .footer__block-item--text .rte a,
    .footer .footer__block-item--text .rte a span {
      color: var(--footer-body-text-color) !important;
    }

    .footer .footer__block-item--text .rte a:hover,
    .footer .footer__block-item--text .rte a:focus,
    .footer .footer__block-item--text .rte a:hover span,
    .footer .footer__block-item--text .rte a:focus span {
      color: var(--footer-accent-color) !important;
    }
  `;

  document.head.appendChild(style);
})();

/**
 * DC.ONE footer contact email correction
 */
(() => {
  function correctFooterEmail() {
    document
      .querySelectorAll('.footer a[href^="mailto:"]')
      .forEach((link) => {
        if (
          link.href.toLowerCase().includes('info@dc1.co.za') ||
          link.textContent.trim().toLowerCase() === 'online@dc1.co.za'
        ) {
          link.href = 'mailto:info@dc1.co.za';
          link.textContent = 'info@dc1.co.za';
        }
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', correctFooterEmail, {
      once: true,
    });
  } else {
    correctFooterEmail();
  }

  document.addEventListener('shopify:section:load', correctFooterEmail);
})();

/**
 * DC.ONE mini-cart layout safety override
 *
 * Keep Checkout as the only full-width action in Warehouse's fixed recap.
 * Move View cart onto an existing recap line so it cannot spill below the drawer.
 */
(() => {
  const STYLE_ID = 'dc-mini-cart-layout-safety-styles';

  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #mini-cart .dc-mini-cart-view-cart {
        display: inline !important;
        width: auto !important;
        min-width: 0 !important;
        min-height: 0 !important;
        height: auto !important;
        margin: 0 0 0 7px !important;
        padding: 0 !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        color: #0b0b0b !important;
        font-size: 11px !important;
        font-weight: 600 !important;
        letter-spacing: 0 !important;
        line-height: inherit !important;
        text-decoration: underline !important;
        text-transform: none !important;
        vertical-align: baseline !important;
      }

      #mini-cart .mini-cart__button-container button[name='checkout'] {
        width: 100% !important;
        margin: 0 !important;
      }
    `;
    document.head.appendChild(style);
  }

  let scheduled = false;

  function fixMiniCartLayout() {
    const cart = document.querySelector('#mini-cart.mini-cart');
    if (!cart) return;

    const group = cart.querySelector(
      '.mini-cart__button-container .button-group',
    );

    const viewCart =
      cart.querySelector('.dc-mini-cart-view-cart') ||
      group?.querySelector("a[href*='/cart']");

    if (!viewCart) return;

    const target =
      cart.querySelector('.mini-cart__amount-saved') ||
      cart.querySelector('.mini-cart__recap-price-line > span:first-child');

    if (!target) return;

    viewCart.classList.remove('button', 'button--secondary');
    viewCart.classList.add('dc-mini-cart-view-cart');

    if (viewCart.parentElement !== target) {
      target.appendChild(viewCart);
    }
  }

  function scheduleFix() {
    if (scheduled) return;

    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      fixMiniCartLayout();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleFix, {
      once: true,
    });
  } else {
    scheduleFix();
  }

  new MutationObserver(scheduleFix).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('cart:rerendered', scheduleFix, true);
  document.documentElement.addEventListener('cart:refresh', scheduleFix, true);
  document.addEventListener('shopify:section:load', scheduleFix, true);
})();
